"use client";

import { useEffect } from "react";
import { Check } from "lucide-react";
import { ConfiguratorFooter } from "@/components/configurator/ConfiguratorFooter";
import { chipStyle } from "@/components/configurator/chip-style";
import { cn } from "@/lib/utils";
import { auroraLineBgClass } from "@/lib/catalog/line-family";
import { useConfigurator } from "@/store/configurator";
import type { StringLineOption, StringTypeOption } from "@/types/configurator";

interface Props {
  onNext: () => void;
  onBack: () => void;
}

function availableTypesFor(
  line: StringLineOption,
  instrumentSlug: string | null,
): StringTypeOption[] {
  return line.stringTypes
    .filter((t) => t.instrumentSlug === null || t.instrumentSlug === instrumentSlug)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

// Lines offered per instrument category, as confirmed in the 2026-09 catalog
// review. Acoustic drops Stainless Steel, classical carries only Ultra-Sheer
// and Aurora One, and ukulele is Aurora One alone.
const LINES_BY_CATEGORY: Record<string, string[]> = {
  electric: ["ultra-sheer", "ultra-flex", "prime", "stainless-steel", "aurora-one"],
  acoustic: ["ultra-sheer", "ultra-flex", "prime", "aurora-one"],
  bass: ["ultra-sheer", "ultra-flex", "prime", "stainless-steel", "aurora-one"],
  classical: ["ultra-sheer", "aurora-one"],
  ukulele: ["aurora-one"],
};

// Stainless Steel is not built for 7- or 8-string electrics.
const LINES_NOT_OFFERED_BY_STRING_COUNT: Record<string, Record<number, string[]>> = {
  electric: {
    7: ["stainless-steel"],
    8: ["stainless-steel"],
  },
};

function linesForInstrument(
  lines: StringLineOption[],
  category: string | null | undefined,
  stringCount: number | null,
): StringLineOption[] {
  const allow = category ? LINES_BY_CATEGORY[category] : undefined;
  const allowed = allow ? lines.filter((l) => allow.includes(l.slug)) : lines;
  const denied =
    category && stringCount !== null
      ? LINES_NOT_OFFERED_BY_STRING_COUNT[category]?.[stringCount]
      : undefined;
  return denied ? allowed.filter((l) => !denied.includes(l.slug)) : allowed;
}

export function LineStep({ onNext, onBack }: Props) {
  const { state, referenceData, setLine, setStringType } = useConfigurator();

  const category = referenceData.instruments.find((i) => i.slug === state.instrumentId)?.category;
  const lines = linesForInstrument(referenceData.lines, category, state.stringCount);

  const selectedLine = state.lineId
    ? referenceData.lines.find((l) => l.slug === state.lineId) ?? null
    : null;
  const selectedLineTypes = selectedLine
    ? availableTypesFor(selectedLine, state.instrumentId)
    : [];
  const needsTypeChoice = selectedLine !== null && selectedLineTypes.length > 1;
  const canProceed = !!state.lineId && !!state.stringTypeId;

  function selectLine(line: StringLineOption) {
    const types = availableTypesFor(line, state.instrumentId);
    setLine(line.slug);
    if (types.length === 1) {
      setStringType(types[0].slug);
    }
  }

  // Smart default: preselect Aurora One (the signature line) so a user can move
  // through without a forced click. Still fully switchable below.
  const defaultLine =
    lines.find((l) => l.slug === "aurora-one") ?? lines[0];
  // Also re-selects when the current line stops being offered — switching a
  // 6-string electric to 7-string drops Stainless Steel from the lineup.
  useEffect(() => {
    const stillOffered = lines.some((l) => l.slug === state.lineId);
    if (!stillOffered && defaultLine) {
      selectLine(defaultLine);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.lineId, defaultLine?.slug, lines.length]);

  function selectType(type: StringTypeOption) {
    setStringType(type.slug);
  }

  return (
    <div>
      <h2
        className="text-2xl font-black mb-2"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Choose your string line.
      </h2>
      <p className="text-sm mb-8" style={{ color: "#9A9A9A" }}>
        Different materials, different color counts, different feels.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {lines.map((line) => {
          const selected = state.lineId === line.slug;
          const types = availableTypesFor(line, state.instrumentId);
          const singleType = types.length === 1 ? types[0] : null;
          const selectedType = selected
            ? types.find((t) => t.slug === state.stringTypeId) ?? null
            : null;
          const selectedTypeBlurb = selectedType?.tagline ?? selectedType?.description ?? null;
          return (
            // div+role, not <button>: the selected card hosts the nested
            // string-type buttons, and buttons can't contain buttons.
            <div
              key={line.id}
              role="button"
              tabIndex={0}
              onClick={() => selectLine(line)}
              onKeyDown={(e) => {
                if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  selectLine(line);
                }
              }}
              aria-pressed={selected}
              className={cn(
                "relative flex flex-col p-6 rounded-md border text-left transition-all cursor-pointer hover:border-[#E8C547] hover:scale-[1.01] overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]",
                auroraLineBgClass(line.slug),
              )}
              style={{
                backgroundColor: selected ? undefined : "#141414",
                borderColor: selected ? "#E8C547" : "#2A2A2A",
                boxShadow: selected ? "0 0 0 1px #E8C54766 inset" : undefined,
              }}
            >
              <div className="line-veil" aria-hidden />
              {selected ? (
                <span
                  className="absolute top-3 left-3 z-10 flex h-7 w-7 items-center justify-center rounded-full"
                  style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
                  aria-label={`${line.name} selected`}
                >
                  <Check size={15} aria-hidden />
                </span>
              ) : null}
              {line.recommended ? (
                <span
                  className="absolute top-3 right-3 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded"
                  style={{ backgroundColor: "#E8C547", color: "#0A0A0A", position: "absolute", zIndex: 3 }}
                >
                  Popular
                </span>
              ) : null}

              <p
                className={cn("text-lg font-black mb-1", selected && "pl-9")}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {line.name}
              </p>
              {line.tagline ?? line.baseMaterial ? (
                <p className="text-sm font-medium mb-2" style={{ color: "#E8C547" }}>
                  {line.tagline ?? line.baseMaterial}
                </p>
              ) : null}
              {line.description ? (
                <p className="text-sm mb-4 flex-1" style={{ color: "#9A9A9A" }}>
                  {line.description}
                </p>
              ) : null}

              {line.features.length > 0 ? (
                <ul className="space-y-1 mb-4">
                  {line.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-xs" style={{ color: "#9A9A9A" }}>
                      <Check size={12} aria-hidden style={{ color: "#40C070", flexShrink: 0 }} /> {feature}
                    </li>
                  ))}
                </ul>
              ) : null}

              {singleType ? (
                <p
                  className="text-[11px] font-semibold uppercase tracking-wider mb-2"
                  style={{ color: "#9A9A9A" }}
                >
                  {singleType.name}
                </p>
              ) : null}

              {/* Multi-type lines (bass): pick the material inside the card */}
              {selected && types.length > 1 ? (
                <div
                  className="relative z-10 mt-1 mb-3 pt-3 border-t"
                  style={{ borderColor: "#E8C54733" }}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "#C0C0C0" }}>
                    String type
                  </p>
                  <div className="flex gap-2" role="radiogroup" aria-label={`String type for ${line.name}`}>
                    {types.map((type) => {
                      const typeSelected = state.stringTypeId === type.slug;
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); selectType(type); }}
                          role="radio"
                          aria-checked={typeSelected}
                          title={type.tagline ?? type.baseMaterial ?? type.name}
                          className="flex-1 px-3 py-2.5 text-xs font-semibold rounded-sm border transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                          style={chipStyle(typeSelected)}
                        >
                          {type.name}
                        </button>
                      );
                    })}
                  </div>
                  {selectedTypeBlurb ? (
                    <p className="text-xs mt-2" style={{ color: "#9A9A9A" }}>
                      {selectedTypeBlurb}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {line.priceHint ? (
                <div className="mt-auto">
                  <p className="text-sm font-bold" style={{ color: selected ? "#E8C547" : "#8A8A8A" }}>
                    {line.priceHint}
                  </p>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <ConfiguratorFooter
        onBack={onBack}
        onNext={onNext}
        nextDisabled={!canProceed}
        nextHint={needsTypeChoice && !state.stringTypeId ? "Choose a string type to continue" : "Choose a string line to continue"}
      />
    </div>
  );
}
